import { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../../config/api';
import {
  Bot, TrendingUp, Users, Music, FileText, Radio, Eye, MousePointer,
  DollarSign, Loader2, RefreshCw, CheckCircle, XCircle, AlertCircle,
  Sparkles, BarChart3, Zap, Shield, Tag, Copy, Bell
} from 'lucide-react';

const SYSTEM_ICONS = {
  scoring_engine: BarChart3,
  featured_engine: Sparkles,
  chart_engine: TrendingUp,
  promotion_expiry: DollarSign,
  anti_spam: Shield,
  content_moderation: Shield,
  auto_tagging: Tag,
  duplicate_detection: Copy,
  chart_notifications: Bell,
  discovery_refresh: RefreshCw,
};

export default function AutomationDashboard() {
  const { getAuthHeaders } = useAuth();
  const [insights, setInsights] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('insights');
  const [actionLoading, setActionLoading] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    const headers = getAuthHeaders();
    try {
      const [insRes, stRes] = await Promise.all([
        fetch(`${API}/automation/insights`, { headers }),
        fetch(`${API}/automation/status`, { headers }),
      ]);
      if (insRes.ok) setInsights(await insRes.json());
      if (stRes.ok) setStatus(await stRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const runAction = async (endpoint, label) => {
    setActionLoading(endpoint);
    try {
      const res = await fetch(`${API}${endpoint}`, { method: 'POST', headers: getAuthHeaders() });
      if (res.ok) { const d = await res.json(); toast.success(d.message || `${label} completed`); fetchData(); }
      else { toast.error('Action failed'); }
    } catch { toast.error('Network error'); }
    setActionLoading('');
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  const pg = insights?.platform_growth || {};
  const eng = insights?.engagement || {};
  const rev = insights?.revenue || {};
  const auto = insights?.automation || {};

  return (
    <div data-testid="automation-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2"><Bot className="w-6 h-6 text-purple-400" />AI Platform Automation</h2>
          <p className="text-sm text-muted-foreground mt-1">Automated operations running continuously</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-muted/30 p-1 mb-6">
          <TabsTrigger value="insights" data-testid="tab-insights"><BarChart3 className="w-4 h-4 mr-2" />Insights</TabsTrigger>
          <TabsTrigger value="systems" data-testid="tab-systems"><Zap className="w-4 h-4 mr-2" />Systems</TabsTrigger>
          <TabsTrigger value="actions" data-testid="tab-actions"><Bot className="w-4 h-4 mr-2" />Actions</TabsTrigger>
        </TabsList>

        {/* INSIGHTS TAB */}
        <TabsContent value="insights">
          {/* Platform Growth */}
          <h3 className="font-heading text-lg font-bold mb-3 flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" />Platform Growth</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { label: 'Total Users', value: pg.total_users, icon: Users, color: 'text-blue-400' },
              { label: 'New (7d)', value: pg.new_users_7d, icon: Users, color: 'text-emerald-400' },
              { label: 'New (30d)', value: pg.new_users_30d, icon: Users, color: 'text-green-400' },
              { label: 'Tracks', value: pg.total_tracks, icon: Music, color: 'text-orange-400' },
              { label: 'Posts', value: pg.total_posts, icon: FileText, color: 'text-purple-400' },
              { label: 'Streams', value: pg.total_streams, icon: Radio, color: 'text-red-400' },
            ].map(s => (
              <Card key={s.label} className="glass-card border-border p-4">
                <s.icon className={`w-4 h-4 ${s.color} mb-1`} />
                <p className="text-xl font-bold">{s.value || 0}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </Card>
            ))}
          </div>

          {/* Engagement + Revenue */}
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <Card className="glass-card border-border p-5">
              <h4 className="font-heading font-bold mb-3 flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" />Engagement (7d)</h4>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Events This Week</span><span className="font-bold">{eng.events_7d || 0}</span></div>
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Events Last Week</span><span className="font-bold">{eng.events_prev_7d || 0}</span></div>
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Growth</span>
                  <Badge className={eng.growth_pct >= 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                    {eng.growth_pct >= 0 ? '+' : ''}{eng.growth_pct || 0}%
                  </Badge>
                </div>
                {eng.by_type && Object.entries(eng.by_type).length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-2">By Type:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(eng.by_type).map(([k, v]) => (
                        <Badge key={k} variant="outline" className="text-xs">{k}: {v}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Card>

            <Card className="glass-card border-border p-5">
              <h4 className="font-heading font-bold mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-400" />Revenue</h4>
              <div className="space-y-2">
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Promotion Revenue</span><span className="font-bold text-green-400">{rev.total_promo_revenue_display || '$0.00'}</span></div>
                <div className="flex justify-between"><span className="text-sm text-muted-foreground">Active Promotions</span><span className="font-bold">{rev.active_promotions || 0}</span></div>
              </div>
              <div className="mt-3 pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Automation:</p>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span>Featured Slots</span>
                    <span>{auto.featured_slots_filled || 0}/{auto.featured_slots_total || 0} filled</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Duplicates Flagged</span>
                    <span>{auto.duplicates_flagged || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Engine Status</span>
                    <Badge className={auto.engine_running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}>
                      {auto.engine_running ? 'Running' : 'Stopped'}
                    </Badge>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Top Creators */}
          {insights?.top_creators?.length > 0 && (
            <Card className="glass-card border-border p-5 mb-6">
              <h4 className="font-heading font-bold mb-3 flex items-center gap-2"><Sparkles className="w-4 h-4 text-yellow-400" />Top Creators (7d)</h4>
              <div className="space-y-2">
                {insights.top_creators.map((c, i) => (
                  <div key={c.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                    <span className="font-bold text-sm w-6 text-center text-muted-foreground">#{i + 1}</span>
                    <div className="w-8 h-8 rounded-full bg-muted overflow-hidden flex-shrink-0">
                      {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover" /> : <Users className="w-4 h-4 text-muted-foreground m-2" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{c.name}</p>
                      <p className="text-xs text-muted-foreground">@{c.handle}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">{c.role}</Badge>
                    <span className="font-bold text-sm text-yellow-400">{c.score_7d?.toFixed(1)}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Role Distribution */}
          {insights?.role_distribution && Object.keys(insights.role_distribution).length > 0 && (
            <Card className="glass-card border-border p-5">
              <h4 className="font-heading font-bold mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-blue-400" />Role Distribution</h4>
              <div className="flex flex-wrap gap-3">
                {Object.entries(insights.role_distribution).map(([role, count]) => (
                  <Badge key={role} variant="outline" className="text-sm py-1 px-3">{role}: {count}</Badge>
                ))}
              </div>
            </Card>
          )}
        </TabsContent>

        {/* SYSTEMS TAB */}
        <TabsContent value="systems">
          <div className="grid gap-3">
            {status?.systems && Object.entries(status.systems).map(([key, sys]) => {
              const Icon = SYSTEM_ICONS[key] || Zap;
              const isActive = sys.status === 'active';
              const isAvailable = sys.status === 'available';
              return (
                <Card key={key} className="glass-card border-border p-4" data-testid={`system-${key}`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'bg-green-500/10' : isAvailable ? 'bg-blue-500/10' : 'bg-red-500/10'}`}>
                      <Icon className={`w-5 h-5 ${isActive ? 'text-green-400' : isAvailable ? 'text-blue-400' : 'text-red-400'}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium text-sm">{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h4>
                      <p className="text-xs text-muted-foreground">{sys.description}</p>
                    </div>
                    <Badge className={isActive ? 'bg-green-500/20 text-green-400' : isAvailable ? 'bg-blue-500/20 text-blue-400' : 'bg-red-500/20 text-red-400'}>
                      {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : isAvailable ? <AlertCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                      {sys.status}
                    </Badge>
                  </div>
                </Card>
              );
            })}
          </div>

          {status?.last_hour_activity && (
            <Card className="glass-card border-border p-5 mt-6">
              <h4 className="font-heading font-bold mb-3">Last Hour Activity</h4>
              <div className="grid grid-cols-3 gap-4">
                <div><p className="text-2xl font-bold">{status.last_hour_activity.events_tracked}</p><p className="text-xs text-muted-foreground">Events Tracked</p></div>
                <div><p className="text-2xl font-bold">{status.last_hour_activity.notifications_sent}</p><p className="text-xs text-muted-foreground">Notifications</p></div>
                <div><p className="text-2xl font-bold">{status.last_hour_activity.impressions_tracked}</p><p className="text-xs text-muted-foreground">Impressions</p></div>
              </div>
            </Card>
          )}
        </TabsContent>

        {/* ACTIONS TAB */}
        <TabsContent value="actions">
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { endpoint: '/admin/stats/force-refresh', label: 'Force Refresh Featured', desc: 'Recompute all featured slots and charts now', icon: RefreshCw, color: 'text-purple-400' },
              { endpoint: '/automation/batch-tag', label: 'Batch Auto-Tag', desc: 'Run AI tagging on all untagged content', icon: Tag, color: 'text-blue-400' },
              { endpoint: '/automation/detect-duplicates', label: 'Detect Duplicates', desc: 'Scan for duplicate posts in last 24h', icon: Copy, color: 'text-amber-400' },
              { endpoint: '/admin/stats/discovery-refresh', label: 'Refresh Discovery', desc: 'Rebuild suggested creators and recommendations', icon: Users, color: 'text-green-400' },
            ].map(action => (
              <Card key={action.endpoint} className="glass-card border-border p-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-muted/30 flex items-center justify-center flex-shrink-0">
                    <action.icon className={`w-5 h-5 ${action.color}`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-sm">{action.label}</h4>
                    <p className="text-xs text-muted-foreground mt-1">{action.desc}</p>
                    <Button size="sm" variant="outline" className="mt-3" onClick={() => runAction(action.endpoint, action.label)}
                      disabled={actionLoading === action.endpoint} data-testid={`action-${action.endpoint.split('/').pop()}`}>
                      {actionLoading === action.endpoint ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                      Run Now
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
